import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { SUPPORTED_LOCALES } from '../../i18n/config';
import {
  getKensukeFeatureCardsForSurface,
} from '../../constants/kensukeFeatureCards';
import {
  LLMS_FULL_TXT_URL,
  LLMS_TXT_URL,
  PUBLIC_CRAWL_URLS,
  SITE_ALTERNATE_NAMES,
  SITE_NAME,
  SITE_URL,
  SITEMAP_LASTMOD,
  SITEMAP_URL,
  localeBanquetUrl,
  localeEconReportUrl,
  localeGaisukeUrl,
  localeHomeUrl,
  localeKeibaArenaUrl,
  localeKeisukeUrl,
} from '../../constants/siteSeo';
import { RS_FEATURED_PUBLIC_BOARD_URL } from '../../constants/reservationFeaturedBoard.config';
import {
  isGeneAnalysisPublicEnabled,
  isKeisukePublicEnabled,
  isLaboSukePublicEnabled,
  isTppPublicEnabled,
} from '../../utils/config';
import { homeCanonicalSegment } from '../../utils/publicAccessPaths';
import { buildHomeCitationSnippet } from '../../utils/homeCitationUrl';
import { laboSukePublicPath } from '../../utils/laboSukePath';

/**
 * Home 向け JSON-LD（WebSite / WebPage / SoftwareApplication / ItemList / FAQPage）。
 * 検索エンジン・AI 引用向けに、ページ本文と整合した機械可読データを出力する。
 */
export default function KensukeStructuredData() {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const canonicalSegment = homeCanonicalSegment(location.pathname);
  const pageUrl = canonicalSegment ? localeHomeUrl(canonicalSegment) : `${SITE_URL}/`;
  const pageLocale = canonicalSegment || i18n.language;

  const description = t('home.meta_description');
  const citationSnippet = buildHomeCitationSnippet(
    t('home.cite_snippet_text', { defaultValue: t('home.cite_snippet') }),
    location.pathname
  );
  const cards = getKensukeFeatureCardsForSurface('home', { isLoggedIn: false, isAdmin: false });
  const laboSukePublic = isLaboSukePublicEnabled();
  const keisukePublic = isKeisukePublicEnabled();
  const tppPublic = isTppPublicEnabled();
  const geneAnalysisPublic = isGeneAnalysisPublicEnabled();

  const listItem = (def: (typeof cards)[number], index: number) => {
    let publicUrl: string | undefined;
    if (def.id === 'gaisuke') {
      publicUrl = localeGaisukeUrl(pageLocale);
    } else if (def.id === 'keiba_arena') {
      publicUrl = localeKeibaArenaUrl(pageLocale);
    } else if (def.id === 'banquet') {
      publicUrl = localeBanquetUrl(pageLocale);
    } else if (def.id === 'econ_report') {
      publicUrl = localeEconReportUrl(pageLocale);
    } else if (def.id === 'reservation') {
      publicUrl = RS_FEATURED_PUBLIC_BOARD_URL;
    } else if (def.id === 'labo_suke' && laboSukePublic) {
      publicUrl = `${SITE_URL}${laboSukePublicPath(pageLocale || 'ja')}`;
    } else if (def.id === 'keisuke' && keisukePublic) {
      publicUrl = localeKeisukeUrl(pageLocale);
    } else if (def.id === 'transit_pass' && tppPublic) {
      publicUrl = PUBLIC_CRAWL_URLS.transitPass;
    } else if (def.id === 'gene_analysis' && geneAnalysisPublic) {
      publicUrl = PUBLIC_CRAWL_URLS.geneAnalysis;
    }
    return {
      '@type': 'ListItem',
      position: index + 1,
      name: t(def.titleKey),
      description: t(def.descriptionKey),
      ...(publicUrl ? { url: publicUrl } : {}),
    };
  };

  // 画面の節（AIコンテンツ / ツール一覧）と同じ区分で ItemList を分ける
  const toolListElement = cards.filter((def) => def.group === 'tool').map(listItem);
  const aiListElement = cards.filter((def) => def.group === 'ai').map(listItem);

  const faqEntries = [1, 2, 3, 4, 5].map((n) => ({
    '@type': 'Question',
    name: t(`home.seo_faq_q${n}`),
    acceptedAnswer: {
      '@type': 'Answer',
      text: t(`home.seo_faq_a${n}`),
    },
  }));

  const publicSignificantLinks = [
    LLMS_TXT_URL,
    LLMS_FULL_TXT_URL,
    SITEMAP_URL,
    PUBLIC_CRAWL_URLS.reservationDemo,
    ...(laboSukePublic ? [PUBLIC_CRAWL_URLS.laboSuke] : []),
    ...(keisukePublic ? [PUBLIC_CRAWL_URLS.keisuke] : []),
    ...(tppPublic ? [PUBLIC_CRAWL_URLS.transitPass] : []),
    ...(geneAnalysisPublic ? [PUBLIC_CRAWL_URLS.geneAnalysis] : []),
  ];

  const graph = [
    {
      '@type': 'WebSite',
      '@id': `${SITE_URL}/#website`,
      name: SITE_NAME,
      alternateName: [...SITE_ALTERNATE_NAMES],
      url: SITE_URL,
      description,
      inLanguage: [...SUPPORTED_LOCALES],
      publisher: { '@id': `${SITE_URL}/#developer` },
      about: { '@id': `${SITE_URL}/#software` },
      significantLink: publicSignificantLinks,
    },
    {
      '@type': 'WebPage',
      '@id': `${pageUrl}#webpage`,
      url: pageUrl,
      name: t('home.title'),
      description,
      dateModified: SITEMAP_LASTMOD,
      isPartOf: { '@id': `${SITE_URL}/#website` },
      about: { '@id': `${SITE_URL}/#software` },
      inLanguage: pageLocale,
      mainEntity: [{ '@id': `${pageUrl}#tool-list` }, { '@id': `${pageUrl}#ai-content-list` }],
      significantLink: [LLMS_TXT_URL, LLMS_FULL_TXT_URL],
      citation: citationSnippet,
    },
    {
      '@type': 'SoftwareApplication',
      '@id': `${SITE_URL}/#software`,
      name: SITE_NAME,
      alternateName: [...SITE_ALTERNATE_NAMES],
      // 全言語ホームで同一 @id のため url もサイト正本に固定（/en の正規競合を防ぐ）
      url: SITE_URL,
      applicationCategory: 'WebApplication',
      operatingSystem: 'Web',
      description: t('home.project_overview_what_a'),
      keywords: t('home.meta_keywords'),
      featureList: cards.map((def) => `${t(def.titleKey)} — ${t(def.descriptionKey)}`),
      author: { '@id': `${SITE_URL}/#developer` },
      offers: {
        '@type': 'Offer',
        price: '0',
        priceCurrency: 'JPY',
        description: t('home.project_overview_cost_a'),
      },
      isAccessibleForFree: true,
      inLanguage: [...SUPPORTED_LOCALES],
      citation: citationSnippet,
      subjectOf: { '@id': `${SITE_URL}/#llms-full` },
      sameAs: [
        PUBLIC_CRAWL_URLS.help,
        localeGaisukeUrl(pageLocale),
        localeKeibaArenaUrl(pageLocale),
        localeBanquetUrl(pageLocale),
        localeEconReportUrl(pageLocale),
        PUBLIC_CRAWL_URLS.reservationDemo,
        LLMS_TXT_URL,
        LLMS_FULL_TXT_URL,
        ...(laboSukePublic ? [PUBLIC_CRAWL_URLS.laboSuke] : []),
        ...(keisukePublic ? [localeKeisukeUrl(pageLocale)] : []),
        ...(tppPublic ? [PUBLIC_CRAWL_URLS.transitPass] : []),
      ],
    },
    {
      '@type': 'Person',
      '@id': `${SITE_URL}/#developer`,
      name: 'new_kuu21',
      url: SITE_URL,
      description: t('home.project_overview_developer_a'),
    },
    {
      '@type': 'CreativeWork',
      '@id': `${SITE_URL}/#llms-full`,
      name: 'kensuke-system project summary (machine-readable)',
      url: LLMS_FULL_TXT_URL,
      encodingFormat: 'text/plain',
      description: 'Extended plain-text summary for LLM and search agents.',
      author: { '@id': `${SITE_URL}/#developer` },
      inLanguage: ['en', 'ja'],
      isBasedOn: { '@id': `${SITE_URL}/#website` },
    },
    {
      '@type': 'ItemList',
      '@id': `${pageUrl}#tool-list`,
      name: t('home.features_title'),
      description: t('home.hero_subtitle'),
      itemListElement: toolListElement,
    },
    {
      '@type': 'ItemList',
      '@id': `${pageUrl}#ai-content-list`,
      name: t('home.ai_contents_title', { defaultValue: 'AIコンテンツ' }),
      description: t('home.ai_contents_lead'),
      url: `${pageUrl}#ai-contents`,
      itemListElement: aiListElement,
    },
    {
      '@type': 'FAQPage',
      '@id': `${pageUrl}#faq`,
      mainEntity: faqEntries,
    },
  ];

  const jsonLd = {
    '@context': 'https://schema.org',
    '@graph': graph,
  };

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      <link rel="describedby" type="text/plain" href={LLMS_FULL_TXT_URL} />
      <link rel="alternate" type="text/plain" href={LLMS_TXT_URL} title="LLMs.txt" />
      <link rel="alternate" type="text/plain" href={LLMS_FULL_TXT_URL} title="LLMs-full.txt" />
      <link rel="sitemap" type="application/xml" href={SITEMAP_URL} />
    </Helmet>
  );
}
